import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import BasicLayout from "layouts/authentication/components/BasicLayout";
import bgImage from "assets/images/bg-sign-in-basic.jpeg";
import { useAuth } from "../../../context/AuthContext";

function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const inputStyle = {
    width: "100%", padding: "12px", borderRadius: "8px",
    border: "1px solid #d2d6da", fontSize: "14px", outline: "none",
    boxSizing: "border-box",
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password, twoFactorRequired ? totpCode : null);
      if (user.role === "admin" || user.role === "enseignant") {
        navigate("/dashboard");
      }
    } catch (err) {
      const data = err.response?.data;
      // Le serveur demande le code 2FA
      if (data?.two_factor_required) {
        setTwoFactorRequired(true);
        setError("");
      } else {
        setError(data?.message || "Email ou mot de passe incorrect");
        setTwoFactorRequired(false);
        setTotpCode("");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox variant="gradient" bgColor="info" borderRadius="lg"
          coloredShadow="info" mx={2} mt={-3} p={2} mb={1} textAlign="center">
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Association — Espace Cours
          </MDTypography>
          <MDTypography variant="body2" color="white" mt={1}>
            {twoFactorRequired
              ? "Entrez votre code de double authentification"
              : "Connectez-vous à votre espace"}
          </MDTypography>
        </MDBox>

        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={handleSubmit} autoComplete="off">

            {error && (
              <MDBox mb={2} p={1.5} sx={{
                background: "#ffebee", borderRadius: "8px", border: "1px solid #ffcdd2",
              }}>
                <MDTypography variant="caption" color="error" fontWeight="medium">
                  ⚠️ {error}
                </MDTypography>
              </MDBox>
            )}

            {!twoFactorRequired ? (
              <>
                {/* Email + mot de passe */}
                <MDBox mb={2}>
                  <input type="text" placeholder="Email"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    autoComplete="off" style={inputStyle} />
                </MDBox>
                <MDBox mb={2}>
                  <input type="password" placeholder="Mot de passe"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password" style={inputStyle} />
                </MDBox>
              </>
            ) : (
              <>
                {/* Code 2FA */}
                <MDBox mb={3} display="flex" flexDirection="column" alignItems="center" gap={2}>
                  <MDBox sx={{
                    width: 64, height: 64, borderRadius: "50%",
                    background: "linear-gradient(135deg, #1565c0, #1976d2)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Icon sx={{ color: "#fff", fontSize: 32 }}>smartphone</Icon>
                  </MDBox>
                  <MDTypography variant="body2" color="text" textAlign="center">
                    Ouvrez votre application d&apos;authentification et entrez
                    le code à 6 chiffres.
                  </MDTypography>
                  <input
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    autoFocus
                    style={{
                      ...inputStyle,
                      fontSize: "28px", textAlign: "center",
                      letterSpacing: "10px", fontWeight: 700,
                      padding: "16px",
                    }}
                  />
                </MDBox>
                <MDBox mb={1}>
                  <MDTypography
                    variant="caption" color="text"
                    sx={{ cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => {
                      setTwoFactorRequired(false);
                      setTotpCode("");
                      setError("");
                    }}>
                    ← Retour à la connexion
                  </MDTypography>
                </MDBox>
              </>
            )}

            <MDBox mt={2} mb={1}>
              <MDButton variant="gradient" color="info" fullWidth
                type="submit" disabled={loading || (twoFactorRequired && totpCode.length !== 6)}>
                {loading ? "Connexion..." : twoFactorRequired ? "Vérifier le code" : "Se connecter"}
              </MDButton>
            </MDBox>

          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default SignIn;